/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */

export const allowedInInputs = [
    'backspace',
    'Meta+C',
    'Meta+V',
    'Meta+Alt+V',
    'Meta+Shift+Alt+V',
    'Meta+X',
    'Ctrl+c',
    'Ctrl+v',
    'Ctrl+x'
];

export const preventedShortcuts = [
    {
        label: '<Meta>+C',
        shortcut: 'Meta+C',
        platform: 'mac',
        browser: 'all',
        description: 'Copy to clipboard.',
        playground: true,
        action: 'copy'
    },
    {
        label: '<Meta>+V',
        shortcut: 'Meta+V',
        platform: 'mac',
        browser: 'all',
        description: 'Paste from clipboard.',
        playground: true,
        action: 'paste'
    },
    {
        label: '<Meta>+<Alt>+V',
        shortcut: 'Meta+Alt+V',
        platform: 'mac',
        browser: 'all',
        description: 'Paste Style',
        playground: true,
        action: 'paste'
    },
    {
        label: '<Meta>+<Shift>+<Alt>+V',
        shortcut: 'Meta+Shift+Alt+V',
        platform: 'mac',
        browser: 'all',
        description: 'Paste and Match Style',
        playground: true,
        action: 'paste'
    },
    {
        label: '<Meta>+X',
        shortcut: 'Meta+X',
        platform: 'mac',
        browser: 'all',
        description: 'Cut to the clipboard',
        playground: true,
        action: 'cut'
    },
    {
        label: '<Meta>+:',
        shortcut: 'Meta+:',
        platform: 'mac',
        browser: 'all',
        description: 'Show / Hide Spelling and Grammar',
        playground: false
    },
    {
        label: '<Ctrl>+F3',
        shortcut: 'Ctrl+F3',
        platform: 'mac',
        browser: 'all',
        description: 'Opens windows menu / dock.',
        playground: false
    },
    {
        label: '<Ctrl>+F2',
        shortcut: 'Ctrl+F2',
        platform: 'mac',
        browser: 'all',
        description: 'Activate Menu',
        playground: false
    },
    {
        label: '<Ctrl>+F8',
        shortcut: 'Ctrl+F8',
        platform: 'mac',
        browser: 'all',
        description: 'Active Menu Extras',
        playground: false
    },
    {
        label: '<Shift>+<Meta>+Right',
        shortcut: 'Shift+Meta+Right',
        platform: 'mac',
        browser: 'all',
        description: 'Switch Applications',
        playground: false
    },
    {
        label: '<Meta>+<Alt>+W',
        shortcut: 'Meta+Alt+W',
        platform: 'mac',
        browser: 'all',
        description: 'Close application',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+3',
        shortcut: 'Meta+Shift+3',
        platform: 'mac',
        browser: 'all',
        description: 'Take screenshot (entire screen)',
        playground: false,
        action: 'screenshot'
    },
    {
        label: '<Meta>+<Shift>+4',
        shortcut: 'Meta+Shift+4',
        platform: 'mac',
        browser: 'all',
        description: 'Take screenshot (partial / active window)',
        playground: false,
        action: 'screenshot'
    },
    {
        label: '<Meta>+<Shift>+W',
        shortcut: 'Meta+Shift+W',
        platform: 'mac',
        browser: 'all',
        description: 'Closes the current window.',
        playground: false
    },
    {
        label: '<Meta>+M',
        shortcut: 'Meta+M',
        platform: 'mac',
        browser: 'all',
        description: 'Minimizes the window.',
        playground: false
    },
    {
        label: '<Meta>+<Alt>+M',
        shortcut: 'Meta+Alt+M',
        platform: 'mac',
        browser: 'all',
        description: 'Minimize (All)',
        playground: false
    },
    {
        label: 'backspace',
        shortcut: 'backspace',
        platform: 'mac',
        browser: 'chrome',
        description: 'Goes to the previous page in your browsing history for the tab.',
        playground: false
    },
    {
        label: '<Meta>+[',
        shortcut: 'Meta+[',
        platform: 'mac',
        browser: 'chrome',
        description: 'Goes to the previous page in your browsing history for the tab',
        playground: false
    },
    {
        label: '<Meta>+]',
        shortcut: 'Meta+]',
        platform: 'mac',
        browser: 'chrome',
        description: 'Goes to the next page in your browsing history for the tab.',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+?',
        shortcut: 'Meta+Shift+?',
        platform: 'mac',
        browser: 'chrome',
        description: 'Browser Help',
        playground: false
    },
    {
        label: '<Meta>+<Ctrl>+F',
        shortcut: 'Meta+Ctrl+F',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens or Exits current page in full-screen mode. (TAO will enforce full screen.)',
        playground: false
    },
    {
        label: '<Meta>+1',
        shortcut: 'Meta+1',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+2',
        shortcut: 'Meta+2',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+3',
        shortcut: 'Meta+3',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+4',
        shortcut: 'Meta+4',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+5',
        shortcut: 'Meta+5',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+6',
        shortcut: 'Meta+6',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+7',
        shortcut: 'Meta+7',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+8',
        shortcut: 'Meta+8',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+9',
        shortcut: 'Meta+9',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the last tab.',
        playground: false
    },
    {
        label: '<Meta>+D',
        shortcut: 'Meta+D',
        platform: 'mac',
        browser: 'chrome',
        description: 'Saves your current webpage as a bookmark.',
        playground: false
    },
    {
        label: '<Meta>+F',
        shortcut: 'Meta+F',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens the find bar.',
        playground: false
    },
    {
        label: '<Meta>+G',
        shortcut: 'Meta+G',
        platform: 'mac',
        browser: 'chrome',
        description: 'Finds the next match for your input in the find bar.',
        playground: false
    },
    {
        label: '<Meta>+Y',
        shortcut: 'Meta+Y',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens the History page.',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+J',
        shortcut: 'Meta+Shift+J',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens the Downloads page.',
        playground: false
    },
    {
        label: '<Meta>+L',
        shortcut: 'Meta+L',
        platform: 'mac',
        browser: 'chrome',
        description: 'Highlights the URL. (Appears not to work in Full Screen Mode)',
        playground: true
    },
    {
        label: '<Meta>+N',
        shortcut: 'Meta+N',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens a new window.',
        playground: false
    },
    {
        label: '<Meta>+O',
        shortcut: 'Meta+O',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens a file from your computer in Google Chrome.',
        playground: false
    },
    {
        label: '<Meta>+P',
        shortcut: 'Meta+P',
        platform: 'mac',
        browser: 'chrome',
        description: 'Prints your current page.',
        playground: false
    },
    {
        label: '<Meta>+Q',
        shortcut: 'Meta+Q',
        platform: 'mac',
        browser: 'chrome',
        description: 'Quits Google Chrome.',
        playground: false
    },
    {
        label: '<Meta>+R',
        shortcut: 'Meta+R',
        platform: 'mac',
        browser: 'chrome',
        description: 'Reloads your current page.',
        playground: false
    },
    {
        label: '<Meta>+S',
        shortcut: 'Meta+S',
        platform: 'mac',
        browser: 'chrome',
        description: 'Saves your current page.',
        playground: false
    },
    {
        label: '<Meta>+T',
        shortcut: 'Meta+T',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens a new tab.',
        playground: false
    },
    {
        label: '<Meta>+<Alt>+U',
        shortcut: 'Meta+Alt+U',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens the source of your current page.',
        playground: false
    },
    {
        label: '<Meta>+W',
        shortcut: 'Meta+W',
        platform: 'mac',
        browser: 'chrome',
        description: 'Closes the current tab or pop-up.',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+B',
        shortcut: 'Meta+Shift+B',
        platform: 'mac',
        browser: 'chrome',
        description: 'Toggles the bookmarks bar on and off.  (Appears not to work in Full Screen Mode)',
        playground: false
    },
    {
        label: '<Meta>+<Alt>+C',
        shortcut: 'Meta+Alt+C',
        platform: 'mac',
        browser: 'chrome',
        description: 'Open / switch from inspect element mode and browser window',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+D',
        shortcut: 'Meta+Shift+D',
        platform: 'mac',
        browser: 'chrome',
        description: 'Saves all open pages as bookmarks in a new folder.',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+G',
        shortcut: 'Meta+Shift+G',
        platform: 'mac',
        browser: 'chrome',
        description: 'Finds the previous match for your input in the find bar.',
        playground: false
    },
    {
        label: '<Meta>+<Alt>+I',
        shortcut: 'Meta+Alt+I',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens Developer Tools.',
        playground: false
    },
    {
        label: '<Meta>+<Alt>+J',
        shortcut: 'Meta+Alt+J',
        platform: 'mac',
        browser: 'chrome',
        description: 'Open Developer Tools and bring focus to the console',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+M',
        shortcut: 'Meta+Shift+M',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switch between multiple users.',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+N',
        shortcut: 'Meta+Shift+N',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens a new window in incognito mode.',
        playground: false
    },
    {
        label: '<Meta>+<Alt>+B',
        shortcut: 'Meta+Alt+B',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens the bookmark manager.',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+T',
        shortcut: 'Meta+Shift+T',
        platform: 'mac',
        browser: 'chrome',
        description: "Reopens the last tab you've closed.",
        playground: false
    },
    {
        label: '<Meta>+<Shift>+W',
        shortcut: 'Meta+Shift+W',
        platform: 'mac',
        browser: 'chrome',
        description: 'Close Window',
        playground: false
    },
    {
        label: '<Meta>+H',
        shortcut: 'Meta+H',
        platform: 'mac',
        browser: 'chrome',
        description: 'Hides Google Chrome.',
        playground: false
    },
    {
        label: '<Meta>+<Alt>+H',
        shortcut: 'Meta+Alt+H',
        platform: 'mac',
        browser: 'chrome',
        description: 'Hides all other windows.',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+H',
        shortcut: 'Meta+Shift+H',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens your home page in your current tab.',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+I',
        shortcut: 'Meta+Shift+I',
        platform: 'mac',
        browser: 'chrome',
        description: 'Emails your current page.',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+P',
        shortcut: 'Meta+Shift+P',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens the Page Setup dialog.',
        playground: false
    },
    {
        label: '<Meta>+,',
        shortcut: 'Meta+,',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens the Settings page.',
        playground: false
    },
    {
        label: '<Meta>+E',
        shortcut: 'Meta+E',
        platform: 'mac',
        browser: 'chrome',
        description: 'Uses selection for find',
        playground: false
    },
    {
        label: '<Alt>+F4',
        shortcut: 'Alt+F4',
        platform: 'mac',
        browser: 'chrome',
        description: 'Close Window',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+Delete',
        shortcut: 'Meta+Shift+Delete',
        platform: 'mac',
        browser: 'chrome',
        description: 'Opens the Clear Browsing Data dialog.',
        playground: false
    },
    {
        label: '<Meta>+<Alt>+Right',
        shortcut: 'Meta+Alt+Right',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the next tab.',
        playground: false
    },
    {
        label: '<Meta>+<Alt>+Left',
        shortcut: 'Meta+Alt+Left',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the previous tab.',
        playground: false
    },
    {
        label: '<Ctrl>+pagedown',
        shortcut: 'Ctrl+pagedown',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the next tab.',
        playground: false
    },
    {
        label: '<Ctrl>+pageup',
        shortcut: 'Ctrl+pageup',
        platform: 'mac',
        browser: 'chrome',
        description: 'Switches to the previous tab.',
        playground: false
    },
    {
        label: '<Alt>+home',
        shortcut: 'Alt+home',
        platform: 'mac',
        browser: 'chrome',
        description: 'Open Home Page',
        playground: false
    },
    {
        label: 'F5',
        shortcut: 'F5',
        platform: 'mac',
        browser: 'firefox',
        description: 'Reloads your current page.',
        playground: false
    },
    {
        label: '<Meta>+1',
        shortcut: 'Meta+1',
        platform: 'mac',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+2',
        shortcut: 'Meta+2',
        platform: 'mac',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+3',
        shortcut: 'Meta+3',
        platform: 'mac',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+4',
        shortcut: 'Meta+4',
        platform: 'mac',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+5',
        shortcut: 'Meta+5',
        platform: 'mac',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+6',
        shortcut: 'Meta+6',
        platform: 'mac',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+7',
        shortcut: 'Meta+7',
        platform: 'mac',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+8',
        shortcut: 'Meta+8',
        platform: 'mac',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Meta>+9',
        shortcut: 'Meta+9',
        platform: 'mac',
        browser: 'firefox',
        description: 'Switches to the last tab.',
        playground: false
    },
    {
        label: '<Meta>+B',
        shortcut: 'Meta+B',
        platform: 'mac',
        browser: 'firefox',
        description: 'Bookmarks Sidebar (toggle)',
        playground: false
    },
    {
        label: '<Meta>+D',
        shortcut: 'Meta+D',
        platform: 'mac',
        browser: 'firefox',
        description: 'Add bookmark',
        playground: false
    },
    {
        label: '<Meta>+F',
        shortcut: 'Meta+F',
        platform: 'mac',
        browser: 'firefox',
        description: 'Find in This Page',
        playground: false
    },
    {
        label: '<Meta>+G',
        shortcut: 'Meta+G',
        platform: 'mac',
        browser: 'firefox',
        description: 'Find Again',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+H',
        shortcut: 'Meta+Shift+H',
        platform: 'mac',
        browser: 'firefox',
        description: 'Browsing History (toggle)',
        playground: false
    },
    {
        label: '<Meta>+J',
        shortcut: 'Meta+J',
        platform: 'mac',
        browser: 'firefox',
        description: 'Download History',
        playground: false
    },
    {
        label: '<Meta>+K',
        shortcut: 'Meta+K',
        platform: 'mac',
        browser: 'firefox',
        description: 'Select Search Bar',
        playground: false
    },
    {
        label: '<Meta>+L',
        shortcut: 'Meta+L',
        platform: 'mac',
        browser: 'firefox',
        description: 'Select Location Bar',
        playground: false
    },
    {
        label: '<Meta>+N',
        shortcut: 'Meta+N',
        platform: 'mac',
        browser: 'firefox',
        description: 'Open new window.',
        playground: false
    },
    {
        label: '<Meta>+O',
        shortcut: 'Meta+O',
        platform: 'mac',
        browser: 'firefox',
        description: 'Open File',
        playground: false
    },
    {
        label: '<Meta>+P',
        shortcut: 'Meta+P',
        platform: 'mac',
        browser: 'firefox',
        description: 'Prints your current page.',
        playground: false
    },
    {
        label: '<Meta>+R',
        shortcut: 'Meta+R',
        platform: 'mac',
        browser: 'firefox',
        description: 'Reloads your current page.',
        playground: false
    },
    {
        label: '<Meta>+S',
        shortcut: 'Meta+S',
        platform: 'mac',
        browser: 'firefox',
        description: 'Save Page As',
        playground: false
    },
    {
        label: '<Meta>+T',
        shortcut: 'Meta+T',
        platform: 'mac',
        browser: 'firefox',
        description: 'Open new tab.',
        playground: false
    },
    {
        label: '<Meta>+U',
        shortcut: 'Meta+U',
        platform: 'mac',
        browser: 'firefox',
        description: 'Page Source',
        playground: false
    },
    {
        label: '<Meta>+W',
        shortcut: 'Meta+W',
        platform: 'mac',
        browser: 'firefox',
        description: 'Close Tab (close Window if only one tab)',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+G',
        shortcut: 'Meta+Shift+G',
        platform: 'mac',
        browser: 'firefox',
        description: 'Find Previous',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+R',
        shortcut: 'Meta+Shift+R',
        platform: 'mac',
        browser: 'firefox',
        description: 'Force Reload Page (override cache)',
        playground: false
    },
    {
        label: '<Alt>+F4',
        shortcut: 'Alt+F4',
        platform: 'mac',
        browser: 'firefox',
        description: 'Close Window',
        playground: false
    },
    {
        label: '<Alt>+return',
        shortcut: 'Alt+return',
        platform: 'mac',
        browser: 'firefox',
        description: 'Save Link Target As',
        playground: true
    },
    {
        label: '<Alt>+home',
        shortcut: 'Alt+home',
        platform: 'mac',
        browser: 'firefox',
        description: 'Open Home Page',
        playground: false
    },
    {
        label: '<Meta>+Left',
        shortcut: 'Meta+Left',
        platform: 'mac',
        browser: 'firefox',
        description: 'Back one Page',
        playground: false
    },
    {
        label: '<Meta>+Right',
        shortcut: 'Meta+Right',
        platform: 'mac',
        browser: 'firefox',
        description: 'Forward one Page',
        playground: false
    },
    {
        label: '<Meta>+[',
        shortcut: 'Meta+[',
        platform: 'mac',
        browser: 'firefox',
        description: 'Back one Page',
        playground: false
    },
    {
        label: '<Meta>+]',
        shortcut: 'Meta+]',
        platform: 'mac',
        browser: 'firefox',
        description: 'Forward one Page',
        playground: false
    },
    {
        label: '<Meta>+return',
        shortcut: 'Meta+return',
        platform: 'mac',
        browser: 'firefox',
        description: 'Open Link (in new Background Tab)',
        playground: true
    },
    {
        label: '<Meta>+F4',
        shortcut: 'Meta+F4',
        platform: 'mac',
        browser: 'firefox',
        description: 'Closes the current tab or pop-up.',
        playground: false
    },
    {
        label: '<Meta>+F5',
        shortcut: 'Meta+F5',
        platform: 'mac',
        browser: 'firefox',
        description: 'Force Reload Page (override cache)',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+T',
        shortcut: 'Meta+Shift+T',
        platform: 'mac',
        browser: 'firefox',
        description: 'Undo Close Tab',
        playground: false
    },
    {
        label: '<Ctrl>+tab',
        shortcut: 'Ctrl+tab',
        platform: 'mac',
        browser: 'firefox',
        description: 'Next Tab',
        playground: false
    },
    {
        label: '<Ctrl>+pagedown',
        shortcut: 'Ctrl+pagedown',
        platform: 'mac',
        browser: 'firefox',
        description: 'Switches to the next tab.',
        playground: false
    },
    {
        label: '<Ctrl>+pageup',
        shortcut: 'Ctrl+pageup',
        platform: 'mac',
        browser: 'firefox',
        description: 'Switches to the previous tab.',
        playground: false
    },
    {
        label: '<Meta>+<Shift>+W',
        shortcut: 'Meta+Shift+W',
        platform: 'mac',
        browser: 'firefox',
        description: 'Close Window',
        playground: false
    },
    {
        label: '<Shift>+F3',
        shortcut: 'Shift+F3',
        platform: 'mac',
        browser: 'firefox',
        description: 'Find Previous',
        playground: false
    },
    {
        label: '<Shift>+return',
        shortcut: 'Shift+return',
        platform: 'mac',
        browser: 'firefox',
        description: 'Open Link in New Window',
        playground: true
    },
    {
        label: 'Quick Find Link',
        shortcut: '',
        platform: 'mac',
        browser: 'firefox',
        description: 'Quick Find Link (not critical)',
        playground: true
    },
    {
        label: '<Ctrl>+c',
        shortcut: 'Ctrl+c',
        platform: 'win',
        browser: 'all',
        description: 'Copy to clipboard.',
        playground: true,
        action: 'copy'
    },
    {
        label: '<Ctrl>+v',
        shortcut: 'Ctrl+v',
        platform: 'win',
        browser: 'all',
        description: 'Paste from clipboard.',
        playground: true,
        action: 'paste'
    },
    {
        label: '<Ctrl>+x',
        shortcut: 'Ctrl+x',
        platform: 'win',
        browser: 'all',
        description: 'Cut to the clipboard',
        playground: true,
        action: 'cut'
    },
    {
        label: '<Ctrl>+esc',
        shortcut: 'Ctrl+esc',
        platform: 'win',
        browser: 'all',
        description: 'Opens windows menu / dock.',
        playground: false
    },
    {
        label: '<Ctrl>+F4',
        shortcut: 'Ctrl+F4',
        platform: 'win',
        browser: 'all',
        description: 'Close the active window.',
        playground: false
    },
    {
        label: '<Shift>+F6',
        shortcut: 'Shift+F6',
        platform: 'win',
        browser: 'all',
        description: "Switches focus to the next keyboard-accessible pane. (Doesn't seem to work in fullscreen mode.)",
        playground: false
    },
    {
        label: '<Alt>+esc',
        shortcut: 'Alt+esc',
        platform: 'win',
        browser: 'all',
        description: 'Switch Applications',
        playground: false
    },
    {
        label: '<Alt>+tab',
        shortcut: 'Alt+tab',
        platform: 'win',
        browser: 'all',
        description: 'Switch Applications',
        playground: false
    },
    {
        label: '<Alt>+space',
        shortcut: 'Alt+space',
        platform: 'win',
        browser: 'all',
        description: 'Open the shortcut menu for the active window',
        playground: true
    },
    {
        label: '<Alt>+F4',
        shortcut: 'Alt+F4',
        platform: 'win',
        browser: 'all',
        description: 'Close application',
        playground: false
    },
    {
        label: 'PrtScn',
        shortcut: 'PrtScn',
        platform: 'win',
        browser: 'all',
        description: 'Take screenshot (entire screen)',
        playground: false,
        action: 'screenshot'
    },
    {
        label: '<Alt>+PrtScn',
        shortcut: 'Alt+PrtScn',
        platform: 'win',
        browser: 'all',
        description: 'Take screenshot (partial / active window)',
        playground: false,
        action: 'screenshot'
    },
    {
        label: '<Meta>+D',
        shortcut: 'Meta+D',
        platform: 'win',
        browser: 'all',
        description: 'Minimize (All)',
        playground: false
    },
    {
        label: 'backspace',
        shortcut: 'backspace',
        platform: 'win',
        browser: 'chrome',
        description: 'Goes to the previous page in your browsing history for the tab.',
        playground: false
    },
    {
        label: 'F1',
        shortcut: 'F1',
        platform: 'win',
        browser: 'chrome',
        description: 'Browser Help',
        playground: false
    },
    {
        label: 'F3',
        shortcut: 'F3',
        platform: 'win',
        browser: 'chrome',
        description: 'Finds the next match for your input in the find bar.',
        playground: false
    },
    {
        label: 'F5',
        shortcut: 'F5',
        platform: 'win',
        browser: 'chrome',
        description: 'Reloads your current page.',
        playground: false
    },
    {
        label: 'F6',
        shortcut: 'F6',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches focus to the next keyboard-accessible pane.',
        playground: false
    },
    {
        label: 'F10',
        shortcut: 'F10',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens the Chrome menu Chrome menu',
        playground: false
    },
    {
        label: 'F11',
        shortcut: 'F11',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens or Exits current page in full-screen mode. (TAO will enforce full screen.)',
        playground: false
    },
    {
        label: 'F12',
        shortcut: 'F12',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens Developer Tools.',
        playground: false
    },
    {
        label: '<Ctrl>+1',
        shortcut: 'Ctrl+1',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+2',
        shortcut: 'Ctrl+2',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+3',
        shortcut: 'Ctrl+3',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+4',
        shortcut: 'Ctrl+4',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+5',
        shortcut: 'Ctrl+5',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+6',
        shortcut: 'Ctrl+6',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+7',
        shortcut: 'Ctrl+7',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+8',
        shortcut: 'Ctrl+8',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+9',
        shortcut: 'Ctrl+9',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the last tab.',
        playground: false
    },
    {
        label: '<Ctrl>+d',
        shortcut: 'Ctrl+d',
        platform: 'win',
        browser: 'chrome',
        description: 'Saves your current webpage as a bookmark.',
        playground: false
    },
    {
        label: '<Ctrl>+e',
        shortcut: 'Ctrl+e',
        platform: 'win',
        browser: 'chrome',
        description: "Places a '?' in the address bar. (Appears not to work in Full Screen Mode)",
        playground: false
    },
    {
        label: '<Ctrl>+f',
        shortcut: 'Ctrl+f',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens the find bar.',
        playground: false
    },
    {
        label: '<Ctrl>+g',
        shortcut: 'Ctrl+g',
        platform: 'win',
        browser: 'chrome',
        description: 'Finds the next match for your input in the find bar.',
        playground: false
    },
    {
        label: '<Ctrl>+h',
        shortcut: 'Ctrl+h',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens the History page.',
        playground: false
    },
    {
        label: '<Ctrl>+j',
        shortcut: 'Ctrl+j',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens the Downloads page.',
        playground: false
    },
    {
        label: '<Ctrl>+k',
        shortcut: 'Ctrl+k',
        platform: 'win',
        browser: 'chrome',
        description: "Places a '?' in the address bar. (Appears not to work in Full Screen Mode)",
        playground: false
    },
    {
        label: '<Ctrl>+l',
        shortcut: 'Ctrl+l',
        platform: 'win',
        browser: 'chrome',
        description: 'Highlights the URL. (Appears not to work in Full Screen Mode)',
        playground: true
    },
    {
        label: '<Ctrl>+n',
        shortcut: 'Ctrl+n',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens a new window.',
        playground: false
    },
    {
        label: '<Ctrl>+o',
        shortcut: 'Ctrl+o',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens a file from your computer in Google Chrome.',
        playground: false
    },
    {
        label: '<Ctrl>+p',
        shortcut: 'Ctrl+p',
        platform: 'win',
        browser: 'chrome',
        description: 'Prints your current page.',
        playground: false
    },
    {
        label: '<Ctrl>+q',
        shortcut: 'Ctrl+q',
        platform: 'win',
        browser: 'chrome',
        description: 'Quits Google Chrome.',
        playground: false
    },
    {
        label: '<Ctrl>+r',
        shortcut: 'Ctrl+r',
        platform: 'win',
        browser: 'chrome',
        description: 'Reloads your current page.',
        playground: false
    },
    {
        label: '<Ctrl>+s',
        shortcut: 'Ctrl+s',
        platform: 'win',
        browser: 'chrome',
        description: 'Saves your current page.',
        playground: false
    },
    {
        label: '<Ctrl>+t',
        shortcut: 'Ctrl+t',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens a new tab.',
        playground: false
    },
    {
        label: '<Ctrl>+u',
        shortcut: 'Ctrl+u',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens the source of your current page.',
        playground: false
    },
    {
        label: '<Ctrl>+w',
        shortcut: 'Ctrl+w',
        platform: 'win',
        browser: 'chrome',
        description: 'Closes the current tab or pop-up.',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+B',
        shortcut: 'Ctrl+Shift+B',
        platform: 'win',
        browser: 'chrome',
        description: 'Toggles the bookmarks bar on and off.  (Appears not to work in Full Screen Mode)',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+C',
        shortcut: 'Ctrl+Shift+C',
        platform: 'win',
        browser: 'chrome',
        description: 'Open / switch from inspect element mode and browser window',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+D',
        shortcut: 'Ctrl+Shift+D',
        platform: 'win',
        browser: 'chrome',
        description: 'Saves all open pages as bookmarks in a new folder.',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+G',
        shortcut: 'Ctrl+Shift+G',
        platform: 'win',
        browser: 'chrome',
        description: 'Finds the previous match for your input in the find bar.',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+I',
        shortcut: 'Ctrl+Shift+I',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens Developer Tools.',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+J',
        shortcut: 'Ctrl+Shift+J',
        platform: 'win',
        browser: 'chrome',
        description: 'Open Developer Tools and bring focus to the console',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+M',
        shortcut: 'Ctrl+Shift+M',
        platform: 'win',
        browser: 'chrome',
        description: 'Switch between multiple users.',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+N',
        shortcut: 'Ctrl+Shift+N',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens a new window in incognito mode.',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+O',
        shortcut: 'Ctrl+Shift+O',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens the bookmark manager.',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+Q',
        shortcut: 'Ctrl+Shift+Q',
        platform: 'win',
        browser: 'chrome',
        description: 'Quits Google Chrome.',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+T',
        shortcut: 'Ctrl+Shift+T',
        platform: 'win',
        browser: 'chrome',
        description: "Reopens the last tab you've closed.",
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+W',
        shortcut: 'Ctrl+Shift+W',
        platform: 'win',
        browser: 'chrome',
        description: 'Close Window',
        playground: false
    },
    {
        label: '<Alt>+d',
        shortcut: 'Alt+d',
        platform: 'win',
        browser: 'chrome',
        description: 'Highlights the URL. (Appears not to work in Full Screen Mode)',
        playground: true
    },
    {
        label: '<Alt>+e',
        shortcut: 'Alt+e',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens the Chrome menu',
        playground: false
    },
    {
        label: '<Alt>+f',
        shortcut: 'Alt+f',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens the Chrome menu',
        playground: false
    },
    {
        label: '<Alt>+F4',
        shortcut: 'Alt+F4',
        platform: 'win',
        browser: 'chrome',
        description: 'Close Window',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+Delete',
        shortcut: 'Ctrl+Shift+Delete',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens the Clear Browsing Data dialog.',
        playground: false
    },
    {
        label: '<Ctrl>+tab',
        shortcut: 'Ctrl+tab',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the next tab.',
        playground: false
    },
    {
        label: '<Ctrl>+shift+tab',
        shortcut: 'Ctrl+shift+tab',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the previous tab.',
        playground: false
    },
    {
        label: '<Ctrl>+pagedown',
        shortcut: 'Ctrl+pagedown',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the next tab.',
        playground: false
    },
    {
        label: '<Ctrl>+pageup',
        shortcut: 'Ctrl+pageup',
        platform: 'win',
        browser: 'chrome',
        description: 'Switches to the previous tab.',
        playground: false
    },
    {
        label: '<Ctrl>+F4',
        shortcut: 'Ctrl+F4',
        platform: 'win',
        browser: 'chrome',
        description: 'Closes the current tab or pop-up.',
        playground: false
    },
    {
        label: '<Ctrl>+F5',
        shortcut: 'Ctrl+F5',
        platform: 'win',
        browser: 'chrome',
        description: 'Reloads your current page, ignoring cached content.',
        playground: false
    },
    {
        label: '<Shift>+esc',
        shortcut: 'Shift+esc',
        platform: 'win',
        browser: 'chrome',
        description: 'Opens the Chrome Task Manager.',
        playground: false
    },
    {
        label: '<Shift>+backspace',
        shortcut: 'Shift+backspace',
        platform: 'win',
        browser: 'chrome',
        description: 'Goes to the next page in your browsing history for the tab.',
        playground: false
    },
    {
        label: '<Shift>+F3',
        shortcut: 'Shift+F3',
        platform: 'win',
        browser: 'chrome',
        description: 'Finds the previous match for your input in the find bar.',
        playground: false
    },
    {
        label: '<Shift>+F5',
        shortcut: 'Shift+F5',
        platform: 'win',
        browser: 'chrome',
        description: 'Reloads your current page, ignoring cached content.',
        playground: false
    },
    {
        label: '<Alt>+left',
        shortcut: 'Alt+left',
        platform: 'win',
        browser: 'chrome',
        description: 'Goes to the previous page in your browsing history for the tab.',
        playground: false
    },
    {
        label: '<Alt>+right',
        shortcut: 'Alt+right',
        platform: 'win',
        browser: 'chrome',
        description: 'Goes to the next page in your browsing history for the tab.',
        playground: false
    },
    {
        label: '<Alt>+home',
        shortcut: 'Alt+home',
        platform: 'win',
        browser: 'chrome',
        description: 'Open Home Page',
        playground: false
    },
    {
        label: 'backspace',
        shortcut: 'backspace',
        platform: 'win',
        browser: 'firefox',
        description: 'Back one page',
        playground: false
    },
    {
        label: 'F1',
        shortcut: 'F1',
        platform: 'win',
        browser: 'firefox',
        description: 'Help',
        playground: false
    },
    {
        label: 'F3',
        shortcut: 'F3',
        platform: 'win',
        browser: 'firefox',
        description: 'Find Again',
        playground: false
    },
    {
        label: 'F5',
        shortcut: 'F5',
        platform: 'win',
        browser: 'firefox',
        description: 'Reloads your current page.',
        playground: false
    },
    {
        label: 'F11',
        shortcut: 'F11',
        platform: 'win',
        browser: 'firefox',
        description: 'Opens or Exits current page in full-screen mode. (TAO will enforce full screen.)',
        playground: false
    },
    {
        label: '<Ctrl>+1',
        shortcut: 'Ctrl+1',
        platform: 'win',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+2',
        shortcut: 'Ctrl+2',
        platform: 'win',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+3',
        shortcut: 'Ctrl+3',
        platform: 'win',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+4',
        shortcut: 'Ctrl+4',
        platform: 'win',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+5',
        shortcut: 'Ctrl+5',
        platform: 'win',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+6',
        shortcut: 'Ctrl+6',
        platform: 'win',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+7',
        shortcut: 'Ctrl+7',
        platform: 'win',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+8',
        shortcut: 'Ctrl+8',
        platform: 'win',
        browser: 'firefox',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+9',
        shortcut: 'Ctrl+9',
        platform: 'win',
        browser: 'firefox',
        description: 'Switches to the last tab.',
        playground: false
    },
    {
        label: '<Ctrl>+b',
        shortcut: 'Ctrl+b',
        platform: 'win',
        browser: 'firefox',
        description: 'Bookmarks Sidebar (toggle)',
        playground: false
    },
    {
        label: '<Ctrl>+d',
        shortcut: 'Ctrl+d',
        platform: 'win',
        browser: 'firefox',
        description: 'Add bookmark',
        playground: false
    },
    {
        label: '<Ctrl>+f',
        shortcut: 'Ctrl+f',
        platform: 'win',
        browser: 'firefox',
        description: 'Find in This Page',
        playground: false
    },
    {
        label: '<Ctrl>+g',
        shortcut: 'Ctrl+g',
        platform: 'win',
        browser: 'firefox',
        description: 'Find Again',
        playground: false
    },
    {
        label: '<Ctrl>+h',
        shortcut: 'Ctrl+h',
        platform: 'win',
        browser: 'firefox',
        description: 'Browsing History (toggle)',
        playground: false
    },
    {
        label: '<Ctrl>+i',
        shortcut: 'Ctrl+i',
        platform: 'win',
        browser: 'firefox',
        description: 'Bookmarks Sidebar (toggle)',
        playground: false
    },
    {
        label: '<Ctrl>+j',
        shortcut: 'Ctrl+j',
        platform: 'win',
        browser: 'firefox',
        description: 'Download History',
        playground: false
    },
    {
        label: '<Ctrl>+k',
        shortcut: 'Ctrl+k',
        platform: 'win',
        browser: 'firefox',
        description: 'Select Search Bar',
        playground: false
    },
    {
        label: '<Ctrl>+l',
        shortcut: 'Ctrl+l',
        platform: 'win',
        browser: 'firefox',
        description: 'Select Location Bar',
        playground: false
    },
    {
        label: '<Ctrl>+n',
        shortcut: 'Ctrl+n',
        platform: 'win',
        browser: 'firefox',
        description: 'Open new window.',
        playground: false
    },
    {
        label: '<Ctrl>+o',
        shortcut: 'Ctrl+o',
        platform: 'win',
        browser: 'firefox',
        description: 'Open File',
        playground: false
    },
    {
        label: '<Ctrl>+p',
        shortcut: 'Ctrl+p',
        platform: 'win',
        browser: 'firefox',
        description: 'Prints your current page.',
        playground: false
    },
    {
        label: '<Ctrl>+r',
        shortcut: 'Ctrl+r',
        platform: 'win',
        browser: 'firefox',
        description: 'Reloads your current page.',
        playground: false
    },
    {
        label: '<Ctrl>+s',
        shortcut: 'Ctrl+s',
        platform: 'win',
        browser: 'firefox',
        description: 'Save Page As',
        playground: false
    },
    {
        label: '<Ctrl>+t',
        shortcut: 'Ctrl+t',
        platform: 'win',
        browser: 'firefox',
        description: 'Open new tab.',
        playground: false
    },
    {
        label: '<Ctrl>+u',
        shortcut: 'Ctrl+u',
        platform: 'win',
        browser: 'firefox',
        description: 'Page Source',
        playground: false
    },
    {
        label: '<Ctrl>+w',
        shortcut: 'Ctrl+w',
        platform: 'win',
        browser: 'firefox',
        description: 'Close Tab (close Window if only one tab)',
        playground: false
    },
    {
        label: '<Alt>+d',
        shortcut: 'Alt+d',
        platform: 'win',
        browser: 'firefox',
        description: 'Select Location Bar',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+G',
        shortcut: 'Ctrl+Shift+G',
        platform: 'win',
        browser: 'firefox',
        description: 'Find Previous',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+R',
        shortcut: 'Ctrl+Shift+R',
        platform: 'win',
        browser: 'firefox',
        description: 'Force Reload Page (override cache)',
        playground: false
    },
    {
        label: '<Alt>+F4',
        shortcut: 'Alt+F4',
        platform: 'win',
        browser: 'firefox',
        description: 'Close Window',
        playground: false
    },
    {
        label: '<Alt>+return',
        shortcut: 'Alt+return',
        platform: 'win',
        browser: 'firefox',
        description: 'Save Link Target As',
        playground: true
    },
    {
        label: '<Alt>+home',
        shortcut: 'Alt+home',
        platform: 'win',
        browser: 'firefox',
        description: 'Open Home Page',
        playground: false
    },
    {
        label: '<Alt>+left',
        shortcut: 'Alt+left',
        platform: 'win',
        browser: 'firefox',
        description: 'Back one Page',
        playground: false
    },
    {
        label: '<Alt>+right',
        shortcut: 'Alt+right',
        platform: 'win',
        browser: 'firefox',
        description: 'Forward one Page',
        playground: false
    },
    {
        label: '<Ctrl>+[',
        shortcut: 'Ctrl+[',
        platform: 'win',
        browser: 'firefox',
        description: 'Back one Page',
        playground: false
    },
    {
        label: '<Ctrl>+]',
        shortcut: 'Ctrl+]',
        platform: 'win',
        browser: 'firefox',
        description: 'Forward one Page',
        playground: false
    },
    {
        label: '<Ctrl>+return',
        shortcut: 'Ctrl+return',
        platform: 'win',
        browser: 'firefox',
        description: 'Open Link (in new Background Tab)',
        playground: true
    },
    {
        label: '<Ctrl>+F4',
        shortcut: 'Ctrl+F4',
        platform: 'win',
        browser: 'firefox',
        description: 'Closes the current tab or pop-up.',
        playground: false
    },
    {
        label: '<Ctrl>+F5',
        shortcut: 'Ctrl+F5',
        platform: 'win',
        browser: 'firefox',
        description: 'Force Reload Page (override cache)',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+T',
        shortcut: 'Ctrl+Shift+T',
        platform: 'win',
        browser: 'firefox',
        description: 'Undo Close Tab',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+Del',
        shortcut: 'Ctrl+Shift+Del',
        platform: 'win',
        browser: 'firefox',
        description: 'Clear Private Data',
        playground: false
    },
    {
        label: '<Ctrl>+tab',
        shortcut: 'Ctrl+tab',
        platform: 'win',
        browser: 'firefox',
        description: 'Next Tab',
        playground: false
    },
    {
        label: '<Ctrl>+pagedown',
        shortcut: 'Ctrl+pagedown',
        platform: 'win',
        browser: 'firefox',
        description: 'Switches to the next tab.',
        playground: false
    },
    {
        label: '<Ctrl>+pageup',
        shortcut: 'Ctrl+pageup',
        platform: 'win',
        browser: 'firefox',
        description: 'Switches to the previous tab.',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+W',
        shortcut: 'Ctrl+Shift+W',
        platform: 'win',
        browser: 'firefox',
        description: 'Close Window',
        playground: false
    },
    {
        label: '<Shift>+backspace',
        shortcut: 'Shift+backspace',
        platform: 'win',
        browser: 'firefox',
        description: 'Forward one Page',
        playground: false
    },
    {
        label: '<Shift>+F3',
        shortcut: 'Shift+F3',
        platform: 'win',
        browser: 'firefox',
        description: 'Find Previous',
        playground: false
    },
    {
        label: '<Shift>+return',
        shortcut: 'Shift+return',
        platform: 'win',
        browser: 'firefox',
        description: 'Open Link in New Window',
        playground: true
    },
    {
        label: '<Shift>+MouseScrollDown',
        shortcut: 'Shift+MouseScrollDown',
        platform: 'win',
        browser: 'firefox',
        description: 'Back one Page',
        playground: false
    },
    {
        label: '<Shift>+MouseScrollUp',
        shortcut: 'Shift+MouseScrollUp',
        platform: 'win',
        browser: 'firefox',
        description: 'Forward one Page',
        playground: false
    },
    {
        label: 'Quick Find Link',
        shortcut: '',
        platform: 'win',
        browser: 'firefox',
        description: 'Quick Find Link (not critical)',
        playground: true
    },
    {
        label: 'backspace',
        shortcut: 'backspace',
        platform: 'win',
        browser: 'ie',
        description: 'Go to the previous page',
        playground: false
    },
    {
        label: 'F1',
        shortcut: 'F1',
        platform: 'win',
        browser: 'ie',
        description: 'Display Internet Explorer Help or to display context Help about an item in a dialog box',
        playground: false
    },
    {
        label: 'F3',
        shortcut: 'F3',
        platform: 'win',
        browser: 'ie',
        description: 'Find on this page',
        playground: false
    },
    {
        label: 'F4',
        shortcut: 'F4',
        platform: 'win',
        browser: 'ie',
        description: 'Display a list of addresses that you have typed',
        playground: false
    },
    {
        label: 'F5',
        shortcut: 'F5',
        platform: 'win',
        browser: 'ie',
        description: 'Refresh the current Web page',
        playground: false
    },
    {
        label: 'F11',
        shortcut: 'F11',
        platform: 'win',
        browser: 'ie',
        description: 'Toggle between full-screen and other views in the browser (TAO will enforce full screen.)',
        playground: false
    },
    {
        label: '<Ctrl>+1',
        shortcut: 'Ctrl+1',
        platform: 'win',
        browser: 'ie',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+2',
        shortcut: 'Ctrl+2',
        platform: 'win',
        browser: 'ie',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+3',
        shortcut: 'Ctrl+3',
        platform: 'win',
        browser: 'ie',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+4',
        shortcut: 'Ctrl+4',
        platform: 'win',
        browser: 'ie',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+5',
        shortcut: 'Ctrl+5',
        platform: 'win',
        browser: 'ie',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+6',
        shortcut: 'Ctrl+6',
        platform: 'win',
        browser: 'ie',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+7',
        shortcut: 'Ctrl+7',
        platform: 'win',
        browser: 'ie',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+8',
        shortcut: 'Ctrl+8',
        platform: 'win',
        browser: 'ie',
        description: 'Switches to the tab at the specified position number on the tab strip.',
        playground: false
    },
    {
        label: '<Ctrl>+9',
        shortcut: 'Ctrl+9',
        platform: 'win',
        browser: 'ie',
        description: 'Switches to the last tab.',
        playground: false
    },
    {
        label: '<Ctrl>+b',
        shortcut: 'Ctrl+b',
        platform: 'win',
        browser: 'ie',
        description: 'Opens organize favorites',
        playground: false
    },
    {
        label: '<Ctrl>+d',
        shortcut: 'Ctrl+d',
        platform: 'win',
        browser: 'ie',
        description: 'Add Favorite',
        playground: false
    },
    {
        label: '<Ctrl>+e',
        shortcut: 'Ctrl+e',
        platform: 'win',
        browser: 'ie',
        description: 'Open the Search box (website search may not be possible in FS)',
        playground: false
    },
    {
        label: '<Ctrl>+f',
        shortcut: 'Ctrl+f',
        platform: 'win',
        browser: 'ie',
        description: 'Find on this page',
        playground: false
    },
    {
        label: '<Ctrl>+g',
        shortcut: 'Ctrl+g',
        platform: 'win',
        browser: 'ie',
        description: 'Open Feeds box',
        playground: false
    },
    {
        label: '<Ctrl>+h',
        shortcut: 'Ctrl+h',
        platform: 'win',
        browser: 'ie',
        description: 'Open the History box',
        playground: false
    },
    {
        label: '<Ctrl>+i',
        shortcut: 'Ctrl+i',
        platform: 'win',
        browser: 'ie',
        description: 'Open the Favorites box',
        playground: false
    },
    {
        label: '<Ctrl>+j',
        shortcut: 'Ctrl+j',
        platform: 'win',
        browser: 'ie',
        description: 'View downloads',
        playground: false
    },
    {
        label: '<Ctrl>+k',
        shortcut: 'Ctrl+k',
        platform: 'win',
        browser: 'ie',
        description: 'Open new tab',
        playground: false
    },
    {
        label: '<Ctrl>+n',
        shortcut: 'Ctrl+n',
        platform: 'win',
        browser: 'ie',
        description: 'Open a new window',
        playground: false
    },
    {
        label: '<Ctrl>+o',
        shortcut: 'Ctrl+o',
        platform: 'win',
        browser: 'ie',
        description: 'Open file or web page',
        playground: false
    },
    {
        label: '<Ctrl>+p',
        shortcut: 'Ctrl+p',
        platform: 'win',
        browser: 'ie',
        description: 'Print the current page or active frame',
        playground: false
    },
    {
        label: '<Ctrl>+r',
        shortcut: 'Ctrl+r',
        platform: 'win',
        browser: 'ie',
        description: 'Refresh the current Web page',
        playground: false
    },
    {
        label: '<Ctrl>+s',
        shortcut: 'Ctrl+s',
        platform: 'win',
        browser: 'ie',
        description: 'Save the current page',
        playground: false
    },
    {
        label: '<Ctrl>+t',
        shortcut: 'Ctrl+t',
        platform: 'win',
        browser: 'ie',
        description: 'Open new tab',
        playground: false
    },
    {
        label: '<Ctrl>+u',
        shortcut: 'Ctrl+u',
        platform: 'win',
        browser: 'ie',
        description: 'View source',
        playground: false
    },
    {
        label: '<Ctrl>+w',
        shortcut: 'Ctrl+w',
        platform: 'win',
        browser: 'ie',
        description: 'Close the current window',
        playground: false
    },
    {
        label: '<Alt>+d',
        shortcut: 'Alt+d',
        platform: 'win',
        browser: 'ie',
        description: 'Select the text in the Address box',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+G',
        shortcut: 'Ctrl+Shift+G',
        platform: 'win',
        browser: 'ie',
        description: 'Add to favorites (Feeds)',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+H',
        shortcut: 'Ctrl+Shift+H',
        platform: 'win',
        browser: 'ie',
        description: 'Add to favorites (History)',
        playground: false
    },
    {
        label: '<Ctrl>+<Shift>+I',
        shortcut: 'Ctrl+Shift+I',
        platform: 'win',
        browser: 'ie',
        description: 'Add to favorities (Favorites)',
        playground: false
    },
    {
        label: '<Shift>+F10',
        shortcut: 'Shift+F10',
        platform: 'win',
        browser: 'ie',
        description: 'Display a shortcut menu for a link',
        playground: true
    },
    {
        label: '<Alt>+F4',
        shortcut: 'Alt+F4',
        platform: 'win',
        browser: 'ie',
        description: 'Close the current or all windows',
        playground: false
    },
    {
        label: '<Alt>+return',
        shortcut: 'Alt+return',
        platform: 'win',
        browser: 'ie',
        description: 'Toggle between full-screen and other views in the browser (TAO will enforce full screen.)',
        playground: false
    },
    {
        label: '<Alt>+home',
        shortcut: 'Alt+home',
        platform: 'win',
        browser: 'ie',
        description: 'Go to your Home page',
        playground: false
    },
    {
        label: '<Alt>+left',
        shortcut: 'Alt+left',
        platform: 'win',
        browser: 'ie',
        description: 'Go to the previous page',
        playground: false
    },
    {
        label: '<Alt>+right',
        shortcut: 'Alt+right',
        platform: 'win',
        browser: 'ie',
        description: 'Go to the next page',
        playground: false
    },
    {
        label: '<Ctrl>+F4',
        shortcut: 'Ctrl+F4',
        platform: 'win',
        browser: 'ie',
        description: 'Close current tab',
        playground: false
    },
    {
        label: '<Ctrl>+F5',
        shortcut: 'Ctrl+F5',
        platform: 'win',
        browser: 'ie',
        description:
            'Refresh the current Web page, even if the time stamp for the Web version and your locally stored version are the same',
        playground: false
    },
    {
        label: '<Ctrl>+tab',
        shortcut: 'Ctrl+tab',
        platform: 'win',
        browser: 'ie',
        description: 'Move forward between frames (tabs)',
        playground: false
    },
    {
        label: '<Ctrl>+shift+tab',
        shortcut: 'Ctrl+shift+tab',
        platform: 'win',
        browser: 'ie',
        description: 'Move back between frames (tabs)',
        playground: false
    },
    {
        label: '<Shift>+F3',
        shortcut: 'Shift+F3',
        platform: 'win',
        browser: 'ie',
        description: 'Open find',
        playground: false
    },
    {
        label: '<Shift>+MouseScrollDown',
        shortcut: 'Shift+MouseScrollDown',
        platform: 'win',
        browser: 'ie',
        description: 'Back one Page',
        playground: false
    },
    {
        label: '<Shift>+MouseScrollUp',
        shortcut: 'Shift+MouseScrollUp',
        platform: 'win',
        browser: 'ie',
        description: 'Forward one Page',
        playground: false
    }
];
